import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme
} from '@mui/material';
import KeyOutlinedIcon from '@mui/icons-material/KeyOutlined';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { IssuedPassword } from './types';
import { copyText } from './staffUtils';
import { dialogIconSx, dialogPaperSx, dialogTitleSx, monoBoxSx, primaryButtonSx } from './staffStyles';

interface StaffPasswordDialogProps {
  issued: IssuedPassword | null;
  onClose: () => void;
}

interface CopyRowProps {
  label: string;
  value: string;
}

const CopyRow: React.FC<CopyRowProps> = ({ label, value }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    const done = await copyText(value);
    setCopied(done);
    if (done) window.setTimeout(() => setCopied(false), 1500);
  };

  return (
    <Box>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#64748b', mb: 0.5 }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={monoBoxSx}>{value}</Box>
        </Box>
        <Tooltip title={copied ? 'Скопировано' : 'Скопировать'} arrow>
          <IconButton onClick={handleCopy} sx={{ border: '1px solid #e2e8f0', borderRadius: '8px', color: copied ? '#059669' : '#64748b' }}>
            {copied ? <CheckIcon fontSize="small" /> : <ContentCopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
};

/**
 * Пароль, выданный сервером, показывается один раз: в базе только хэш. Закрыть окно случайным кликом мимо
 * нельзя - только кнопкой «Готово», после того как данные переданы сотруднику.
 */
const StaffPasswordDialog: React.FC<StaffPasswordDialogProps> = ({ issued, onClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Dialog open={Boolean(issued)} maxWidth="xs" fullWidth fullScreen={fullScreen} PaperProps={{ sx: dialogPaperSx }}>
      <DialogTitle sx={dialogTitleSx}>
        <Box sx={dialogIconSx}>
          <KeyOutlinedIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontWeight: 700, color: '#0f172a', fontSize: '1.05rem', lineHeight: 1.25 }}>Данные для входа</Typography>
          {issued && <Typography sx={{ fontSize: '0.8rem', color: '#64748b' }} noWrap>{issued.fullName}</Typography>}
        </Box>
      </DialogTitle>
      {issued && (
        <DialogContent sx={{ pt: '20px !important', display: 'flex', flexDirection: 'column', gap: 2 }}>
          <CopyRow label="Логин" value={issued.login} />
          <CopyRow label="Пароль" value={issued.password} />
          <Alert severity="warning" sx={{ borderRadius: '10px', fontSize: '0.82rem' }}>
            Пароль показывается один раз. Сохраните его или сразу передайте сотруднику.
            {issued.mustChange && ' При первом входе сотрудник задаст свой пароль.'}
          </Alert>
        </DialogContent>
      )}
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button variant="contained" disableElevation onClick={onClose} sx={primaryButtonSx}>
          Готово
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StaffPasswordDialog;
